import { ConfigProvider } from 'antd';
import classNames from 'classnames';
import { createContext } from 'react';
import { createGlobalStyle } from 'antd-style';

import { ThemeColors, ThemeFont } from '../../theme';
import { viewModeStyle } from '../../styles/mixins';
import {
  DARK_BACKGROUND_COLOR,
  DARK_SIMPLE_PLACEHOLDER_COLOR,
  DARK_VIEW_BACKGROUND_COLOR,
  FormTheme,
  ORANGE_PLACEHOLDER_COLOR,
  ThemeBoxShadowMap,
} from '../../styles/formThemeConstants';

// Shared across dark themes
const DARK_DISABLED_BACKGROUND_COLOR = ThemeColors.COLOR_GREY_DARK2_6;
const DARK_ICON_COLOR = ThemeColors.COLOR_GREY_BLUISH2;
const DARK_CHECKBOX_TOKENS = {
  controlInteractiveSize: 22,
  colorBgContainer: ThemeColors.COLOR_GREY_DARK2_7,
  colorBorder: ThemeColors.COLOR_GREY_BLUISH8,
  colorPrimary: ThemeColors.COLOR_GREY_BLUISH8,
  colorPrimaryHover: ThemeColors.COLOR_GREY_BLUISH8,
  colorText: ThemeColors.COLOR_GREY_BLUISH7_4,
  colorTextDisabled: ThemeColors.COLOR_GREY_BLUISH7_4,
  fontFamily: ThemeFont.FONT_CONDENSED,
  fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
};
const DARK_RADIO_TOKENS = {
  colorBorder: ThemeColors.COLOR_GREY_BLUISH8,
  colorBgContainer: ThemeColors.COLOR_GREY_DARK2_7,
  colorPrimary: ThemeColors.COLOR_GREY_BLUISH8,
  colorText: ThemeColors.COLOR_GREY_BLUISH8,
  fontSize: ThemeFont.FONT_SIZE_LG_MINUS,
  wireframe: true,
};

// Shared across orange themes
const ORANGE_TEXT_COLOR = ThemeColors.COLOR_ORANGE1;
const ORANGE_BORDER_COLOR = ThemeColors.COLOR_GREY_DARK2_7;

const ORANGE_INPUT_TOKENS = {
  activeBg: DARK_BACKGROUND_COLOR,
  activeBorderColor: ORANGE_BORDER_COLOR,
  colorBgContainerDisabled: DARK_DISABLED_BACKGROUND_COLOR,
  colorBorder: ORANGE_BORDER_COLOR,
  colorIcon: DARK_ICON_COLOR,
  colorText: ORANGE_TEXT_COLOR,
  colorTextDisabled: ORANGE_TEXT_COLOR,
  colorTextQuaternary: DARK_ICON_COLOR,
  hoverBorderColor: ORANGE_BORDER_COLOR,
  hoverBg: DARK_BACKGROUND_COLOR,
};
const ORANGE_SELECT_TOKENS = {
  activeBorderColor: ORANGE_BORDER_COLOR,
  colorBgContainerDisabled: DARK_DISABLED_BACKGROUND_COLOR,
  colorBorder: ORANGE_BORDER_COLOR,
  colorIcon: DARK_ICON_COLOR,
  colorText: ORANGE_TEXT_COLOR,
  colorTextDisabled: ORANGE_TEXT_COLOR,
  colorTextPlaceholder: ORANGE_PLACEHOLDER_COLOR,
  colorTextQuaternary: DARK_ICON_COLOR,
  hoverBorderColor: ORANGE_BORDER_COLOR,
  selectorBg: DARK_BACKGROUND_COLOR,
};

// Dark Simple
const DARK_SIMPLE_BORDER_COLOR = ThemeColors.COLOR_GREY_DARK5;
const DARK_SIMPLE_TEXT_COLOR = ThemeColors.COLOR_GREY7_1;

const DARK_SELECT_DROPDOWN_TOKENS = {
  colorBgElevated: ThemeColors.COLOR_GREY_DARK5,
  boxShadowSecondary: `0 1px 60px ${ThemeColors.COLOR_BLACK0_7}`,
  optionColor: ThemeColors.COLOR_GREY_BLUISH4,
  optionActiveBg: ThemeColors.COLOR_GREY_DARK2,
  optionSelectedBg: ThemeColors.COLOR_GREY_DARK2,
  optionSelectedColor: ThemeColors.COLOR_GREY_BLUISH7_4,
};

const darkSimpleTheme = {
  components: {
    Checkbox: DARK_CHECKBOX_TOKENS,
    Input: {
      activeBg: DARK_BACKGROUND_COLOR,
      activeBorderColor: DARK_SIMPLE_BORDER_COLOR,
      colorBgContainerDisabled: DARK_DISABLED_BACKGROUND_COLOR,
      colorBorder: DARK_SIMPLE_BORDER_COLOR,
      colorIcon: DARK_ICON_COLOR, // Clear icon color hover
      colorText: DARK_SIMPLE_TEXT_COLOR,
      colorTextDisabled: DARK_SIMPLE_TEXT_COLOR,
      colorTextQuaternary: DARK_ICON_COLOR, // Clear icon color
      hoverBorderColor: DARK_SIMPLE_BORDER_COLOR,
      hoverBg: DARK_BACKGROUND_COLOR,
    },
    Radio: DARK_RADIO_TOKENS,
    Select: {
      activeBorderColor: DARK_SIMPLE_BORDER_COLOR,
      colorBgContainerDisabled: DARK_DISABLED_BACKGROUND_COLOR,
      colorBorder: DARK_SIMPLE_BORDER_COLOR,
      colorIcon: DARK_ICON_COLOR,
      colorText: DARK_SIMPLE_TEXT_COLOR,
      colorTextDisabled: DARK_SIMPLE_TEXT_COLOR,
      colorTextPlaceholder: DARK_SIMPLE_PLACEHOLDER_COLOR,
      colorTextQuaternary: DARK_ICON_COLOR,
      hoverBorderColor: DARK_SIMPLE_BORDER_COLOR,
      selectorBg: DARK_BACKGROUND_COLOR,
      ...DARK_SELECT_DROPDOWN_TOKENS,
    },
  },
};
const darkOrangeTheme = {
  components: {
    Checkbox: DARK_CHECKBOX_TOKENS,
    Input: ORANGE_INPUT_TOKENS,
    Radio: DARK_RADIO_TOKENS,
    Select: {
      ...ORANGE_SELECT_TOKENS,
      ...DARK_SELECT_DROPDOWN_TOKENS,
    },
    Switch: {
      colorPrimary: ThemeColors.COLOR_GREY_BLUISH7_4,
    },
  },
};
const darkOrangeSimpleTheme = {
  components: {
    Checkbox: DARK_CHECKBOX_TOKENS,
    Input: ORANGE_INPUT_TOKENS,
    Radio: DARK_RADIO_TOKENS,
    Select: ORANGE_SELECT_TOKENS,
  },
};

const ThemeMap = {
  [FormTheme.DarkSimple]: darkSimpleTheme,
  [FormTheme.DarkOrange]: darkOrangeTheme,
  [FormTheme.DarkOrangeSimple]: darkOrangeSimpleTheme,
};

function getSelectStyles(boxShadow) {
  return {
    '.ant-select': {
      '.ant-select-selector::after': {
        boxShadow,
      },
      '&.qol-input-view-mode .ant-select-selector': {
        ...viewModeStyle(DARK_VIEW_BACKGROUND_COLOR),
      },
    },
  };
}

function getInputAffixStyles() {
  return {
    '.ant-input-affix-wrapper': {
      backgroundColor: DARK_BACKGROUND_COLOR,
      '&.ant-input-affix-wrapper-disabled': {
        backgroundColor: DARK_DISABLED_BACKGROUND_COLOR,
      },
    },
  };
}

function getCheckboxStyles() {
  return {
    '.ant-checkbox-checked .ant-checkbox-inner::before': {
      color: DARK_CHECKBOX_TOKENS.colorBgContainer,
    },
    '.ant-checkbox-wrapper .ant-checkbox-disabled': {
      opacity: 0.5,
    },
  };
}

function getRadioStyles() {
  return {
    '.ant-radio-wrapper.ant-radio-wrapper-checked': {
      color: ThemeColors.COLOR_ORANGE1,
    },
  };
}

function getSwitchStyles() {
  return {
    '.ant-switch': {
      boxShadow: 'none',
    },
  };
}

const FormThemeStyles = createGlobalStyle(({ themeClassName }) => {
  const boxShadow = ThemeBoxShadowMap[themeClassName];

  return {
    [`.${themeClassName}`]: {
      '.ant-form-item .ant-form-item-control-input .ant-form-item-control-input-content':
        {
          ...getSelectStyles(boxShadow),
          ...getInputAffixStyles(),
          ...getCheckboxStyles(),
          ...getRadioStyles(),
        },
      ...getSwitchStyles(),
    },
  };
});

export const FormThemeOverrideContext = createContext({
  themeClassName: null,
});
export function FormThemeOverrideContextProvider({
  component,
  themeClassName,
  className,
  children,
  ...rest
}) {
  const C = component || 'div';
  const themeConfig = ThemeMap[themeClassName];

  const inner = (
    <FormThemeOverrideContext.Provider value={{ themeClassName }}>
      {themeClassName && <FormThemeStyles themeClassName={themeClassName} />}
      <C {...rest} className={classNames(themeClassName, className)}>
        {children}
      </C>
    </FormThemeOverrideContext.Provider>
  );

  if (!themeConfig) return inner;

  return <ConfigProvider theme={themeConfig}>{inner}</ConfigProvider>;
}
